import jsPDF from "jspdf";
import { htmlToPlainText } from "./htmlToPlainText";
import {
	clientConfig, describeClient, describeEntryLink, downloadBlob,
	formatDate, formatDateTime, formatRange, exportFilename,
	hasLetterhead, winAnsiSafe,
} from "./exportHelpers";
import {
	appendPages, createMergeContext, createTextPageWriter, fetchPdf,
} from "./pdfMerge";

/*
	A4 in points: 595.28 x 841.89. Layout constants match the existing
	single-entry printer (DiaryPrinter.js), which stays untouched --
	this module is self-contained on purpose.
*/
const LEFT = 70;
const RIGHT = 525;
const WIDTH = RIGHT - LEFT;
const TOP = 70;
const BOTTOM = 770;
const LINE = 15;
const PAGE_CENTER = 595.28 / 2;

const cfg = clientConfig;

/**
 * Generate and download one PDF containing all matching entries: one
 * head sheet with the overall information, then each entry with a
 * header carrying the entry-specific data.
 *
 * options:
 *   client          : Parse Client (focused client)
 *   entryLinks      : EntryLink[] already filtered and sorted
 *   from, to        : Date bounds of the export (may be undefined)
 *   attributeNames  : names of the selected filter attributes
 *   exportedBy      : full name of the exporting user
 *   includePictures : embed the entries' attached images
 *   includePdfs     : append the entries' attached PDF documents as an
 *                     "Anlagen" appendix (index page + the documents'
 *                     pages, merged via pdf-lib)
 */
export const printDiaryRangePdf = async (options) => {
	const { client, entryLinks, from, to } = options;
	const doc = new jsPDF({ unit: "pt" });
	const entries = entryLinks.map(describeEntryLink);

	let y = addLetterhead(doc);
	addHeadSheet(doc, y, options);

	// Every entry starts on its own page.
	for (const entry of entries) {
		doc.addPage();
		y = addEntry(doc, TOP, entry);
		if (options.includePictures && entry.pictures.length > 0) {
			await addPictures(doc, y, entry.pictures);
		}
	}

	addPageNumbers(doc);

	const filename = exportFilename(client, from, to, "pdf");
	const attachments = options.includePdfs
		? entries.flatMap((entry) => entry.documents.map((document) => ({
				title: document.title,
				url: document.url,
				entryDate: formatDate(entry.createdAt),
			})))
		: [];
	if (attachments.length === 0) {
		doc.save(filename);
		return;
	}
	const merged =
		await appendPdfAttachments(doc.output("arraybuffer"), attachments);
	downloadBlob(filename, new Blob([ merged ], {
		type: "application/pdf",
	}));
};

function addLetterhead(doc) {
	// No configured letterhead: no address, no logo (never another
	// customer's branding). The head sheet starts at the top instead.
	if (!hasLetterhead()) { return TOP; }
	// Classic layout, fixed line slots at 15pt: the bold address
	// block ends on slot 115 and the contact block on slot 180, so
	// shorter blocks bottom-align against the heading (slot 200).
	// The logo box spans 357..525 x 50..206, its right edge on the
	// text margin; the blobs in client_config.json are padded for
	// exactly this box.
	doc.setFontSize(10);
	const address =
		[ "adress_titel", "adress_titel_zusatz", "adress_strasse",
			"adress_stadt" ].map(cfg).filter(Boolean);
	const contact =
		[ "telefon", "fax", "mail", "web" ].map(cfg).filter(Boolean);
	doc.setFont("Helvetica", "Bold");
	let y = 115 - (address.length - 1) * LINE;
	for (const line of address) {
		doc.text(line, LEFT, y);
		y += LINE;
	}
	doc.setFont("Helvetica", "");
	y = 180 - (contact.length - 1) * LINE;
	for (const line of contact) {
		doc.text(line, LEFT, y);
		y += LINE;
	}
	const logo = cfg("logo");
	if (logo) { doc.addImage(logo, "JPEG", 357, 50, 168, 156); }
	return 200;
}

function addHeadSheet(doc, y, options) {
	const { client, entryLinks, from, to, attributeNames, exportedBy } =
		options;
	const info = describeClient(client);

	doc.setFont("Helvetica", "Bold");
	doc.text("Tagebucheinträge", LEFT, y);
	doc.setFont("Helvetica", "");
	y += 3;
	doc.line(LEFT, y, RIGHT, y);
	y += LINE;
	y = addMetaRow(doc, y, "Name, Vorname:", info.name);
	y = addMetaRow(doc, y, "Gruppe:", info.groupName);
	y = addMetaRow(doc, y, "Geboren am:", info.birthday || "-");
	y = addMetaRow(doc, y, "Hilfebeginn:", info.helpBegin || "-");
	y = addMetaRow(doc, y, "Zeitraum:", formatRange(from, to));
	y = addMetaRow(doc, y, "Attribute:",
		attributeNames && attributeNames.length > 0
			? attributeNames.join(", ") : "Alle");
	y = addMetaRow(doc, y, "Anzahl Einträge:", entryLinks.length);
	y = addMetaRow(doc, y, "Exportiert am:", formatDate(new Date()));
	if (exportedBy) {
		y = addMetaRow(doc, y, "Exportiert von:", exportedBy);
	}
	y -= 10;
	doc.line(LEFT, y, RIGHT, y);
	return y;
}

function addMetaRow(doc, y, label, value) {
	const text = String(value !== undefined ? value : "-");
	doc.text(label, LEFT, y);
	doc.text(text, RIGHT - doc.getTextWidth(text), y);
	return y + LINE;
}

function breakPage(doc) {
	doc.addPage();
	return TOP;
}

function addEntry(doc, y, entry) {
	// Title
	doc.setFont("Helvetica", "Bold");
	doc.setFontSize(13);
	const title = entry.title ||
		("Tagebucheintrag vom " + formatDate(entry.createdAt));
	for (const line of doc.splitTextToSize(title, WIDTH)) {
		doc.text(line, LEFT, y);
		y += 17;
	}

	// Meta lines
	doc.setFont("Helvetica", "");
	doc.setFontSize(10);
	const meta = [];
	if (entry.authorName) { meta.push(entry.authorName); }
	if (entry.createdAt) { meta.push(formatDateTime(entry.createdAt)); }
	if (entry.counter) { meta.push("Eintrag: " + entry.counter); }
	if (entry.typeName) { meta.push("Typ: " + entry.typeName); }
	if (entry.isPrivate) { meta.push("Privat"); }
	y = addWrappedLine(doc, y, meta.join(" · "));
	if (entry.tags.length > 0) {
		y = addWrappedLine(doc, y, "Tags: " + entry.tags.join(", "));
	}
	if (entry.rating !== undefined && entry.rating !== null) {
		y = addWrappedLine(doc, y, "Verhaltensbewertung: " + entry.rating);
	}
	if (entry.attachments) {
		// jsPDF's standard fonts are WinAnsi-encoded too; NFD umlauts in
		// attachment filenames otherwise render with broken glyph widths.
		y = addWrappedLine(doc, y,
			winAnsiSafe("Anhänge: " + entry.attachments));
	}

	// Thin rule under the header
	y -= 9;
	doc.setDrawColor(120);
	doc.line(LEFT, y, RIGHT, y);
	doc.setDrawColor(0);
	y += LINE;

	// Entry text
	const text = htmlToPlainText(entry.html);
	for (const line of doc.splitTextToSize(text, WIDTH)) {
		if (y > BOTTOM) { y = breakPage(doc); }
		doc.text(line, LEFT, y);
		y += LINE;
	}
	return y;
}

function addWrappedLine(doc, y, text) {
	if (!text) { return y; }
	for (const line of doc.splitTextToSize(text, WIDTH)) {
		if (y > BOTTOM) { y = breakPage(doc); }
		doc.text(line, LEFT, y);
		y += LINE;
	}
	return y;
}

function addPageNumbers(doc) {
	const total = doc.getNumberOfPages();
	doc.setFont("Helvetica", "");
	doc.setFontSize(8);
	for (let page = 1; page <= total; page++) {
		doc.setPage(page);
		const label = "Seite " + page + "/" + total;
		doc.text(label, PAGE_CENTER - doc.getTextWidth(label) / 2, 812);
	}
}

/*
	Attached images. Each picture is scaled to the text width (capped to
	the page height) and placed below the entry text; a picture that no
	longer loads becomes a note line instead of failing the export.
*/

async function addPictures(doc, y, pictures) {
	const maxHeight = BOTTOM - TOP;
	for (const picture of pictures) {
		const image = await loadImage(picture.url);
		if (!image) {
			if (y > BOTTOM) { y = breakPage(doc); }
			doc.setFont("Helvetica", "Oblique");
			doc.text(winAnsiSafe("Bild konnte nicht geladen werden: " +
				(picture.name || picture.url)), LEFT, y);
			doc.setFont("Helvetica", "");
			y += LINE;
			continue;
		}
		const ratio = image.naturalHeight / image.naturalWidth;
		let width = WIDTH;
		let height = width * ratio;
		if (height > maxHeight) {
			height = maxHeight;
			width = height / ratio;
		}
		if (y + height > BOTTOM) { y = breakPage(doc); }
		const format =
			/\.png(\?|$)/i.test(picture.url) ? "PNG" : "JPEG";
		doc.addImage(image, format, LEFT, y, width, height, "", "FAST");
		y += height + LINE;
	}
	return y;
}

function loadImage(url) {
	return new Promise((resolve) => {
		const image = new Image();
		// The Parse server answers with open CORS headers; requesting the
		// image anonymously keeps the canvas jsPDF rasterizes on untainted
		// even when the dev server runs on another origin.
		image.crossOrigin = "anonymous";
		image.onload = () => resolve(image);
		image.onerror = () => resolve(undefined);
		image.src = url;
	});
}

/*
	Attached PDF documents. jsPDF cannot merge existing PDFs, so the
	generated document is reloaded with pdf-lib, an "Anlagen" index page
	is added, and every loadable attachment's pages are appended.
	Documents that cannot be fetched or parsed (missing, encrypted, not
	actually a PDF) are marked as such on the index page.
*/

async function appendPdfAttachments(pdfBytes, attachments) {
	const context = await createMergeContext(pdfBytes);
	const results = [];
	for (const attachment of attachments) {
		results.push({ attachment, source: await fetchPdf(attachment.url) });
	}
	addAttachmentIndex(context, results);
	for (const result of results) {
		if (!result.source) { continue; }
		await appendPages(context, result.source);
	}
	return context.merged.save();
}

// Column geometry of the attachment index (x relative to the left
// margin; the text area is 455pt wide).
const ANLAGEN_COLUMNS = {
	title : { x: 0,   width: 320 },
	entry : { x: 330, width: 125 },
};

function addAttachmentIndex(context, results) {
	const writer = createTextPageWriter(context);
	writer.heading("Anlagen");
	writer.row([
		{ text: "Dokument", ...ANLAGEN_COLUMNS.title },
		{ text: "Eintrag vom", ...ANLAGEN_COLUMNS.entry },
	], { bold: true });
	for (const result of results) {
		writer.row([
			{ text: result.attachment.title, ...ANLAGEN_COLUMNS.title },
			{ text: result.attachment.entryDate, ...ANLAGEN_COLUMNS.entry },
		]);
		if (!result.source) {
			writer.line("konnte nicht geladen werden", { indent: 10 });
		}
	}
}
